import { recreateTransform } from '@technik-sde/prosemirror-recreate-transform'
import { Extension, getSchema } from '@tiptap/core'
import { EditorState, Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { ChangeSet, simplifyChanges } from 'prosemirror-changeset'
import { createExtensions } from 'website/src/extensions'
import { atoms } from 'website/src/lib/atoms'
import { schemaGetter } from 'website/src/lib/editor-utils'
import { yDocToJson } from 'website/src/lib/utils'
import {
    yDocToProsemirrorJSON,
    ySyncPlugin,
    ySyncPluginKey,
} from 'y-prosemirror'

const diffKey = new PluginKey('diff')

function debounceWithValueAndPageChange<T extends (...args: any[]) => any>(
    func: T,
    delay: number,
): (...args: Parameters<T>) => ReturnType<T> {
    let timeoutId: NodeJS.Timeout | null = null
    let result: ReturnType<T> | undefined

    let currentPath = ''
    return ((...args: Parameters<T>) => {
        let newPath = window.location.pathname
        if (currentPath !== newPath) {
            result = undefined
            currentPath = newPath
        }
        if (!result) {
            if (timeoutId) {
                clearTimeout(timeoutId)
            }
            result = func(...args)
            timeoutId = setTimeout(() => {
                result = undefined
                timeoutId = null
            }, delay)
        }
        return result
    }) as any
}

const getOldDocNode = debounceWithValueAndPageChange(function getOldDocNode() {
    const oldDoc = atoms.docWithoutAnonymousChanges.get()
    const schema = schemaGetter(window.location.pathname)
    let oldDocNode = schema.nodeFromJSON(yDocToJson(oldDoc))

    return oldDocNode
}, 100)

function createDecorations(newState: EditorState) {
    const docNodeNew = newState.doc
    try {
        const oldDocNode = getOldDocNode()
        let transform = recreateTransform(oldDocNode, docNodeNew)

        let changes = simplifyChanges(
            ChangeSet.create(docNodeNew).addSteps(
                oldDocNode,
                transform.mapping.maps,
                [],
            ).changes,
            transform.doc,
        )
        atoms.localChangesForPR.set(changes.length)
        let speckles: any[] = []
        changes.forEach((insertion) => {
            if (!insertion.inserted) {
                return
            }
            speckles.push(
                Decoration.inline(insertion.fromB, insertion.toB, {
                    class: ' bg-lime-600/50',
                }),
            )
        })
        return DecorationSet.create(docNodeNew, speckles)
    } catch (e) {
        console.error(`diff plugin`, e)
    }
}

export const DiffExtension = Extension.create({
    name: 'diff',
    addOptions() {
        return {}
    },
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: diffKey,
                state: {
                    init(_, state) {
                        return createDecorations(state)
                    },
                    apply(tr, set, old, newState) {
                        return createDecorations(newState)
                    },
                },
                props: {
                    decorations(state) {
                        return this.getState(state)!
                    },
                },
            }),
        ]
    },
})
